"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DISMISS_STORAGE_KEY = "u12-pwa-install-dismissed-until";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

type PromptChoice = {
  outcome: "accepted" | "dismissed";
  platform: string;
};

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<PromptChoice>;
}

declare global {
  interface Window {
    __u12DeferredInstallPrompt?: BeforeInstallPromptEvent | null;
  }
}

function isStandaloneMode() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

function getDismissedUntil() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    const timestamp = value ? Number(value) : NaN;

    return Number.isFinite(timestamp) && timestamp > Date.now() ? timestamp : null;
  } catch {
    return null;
  }
}

function dismissForOneDay() {
  if (typeof window === "undefined") {
    return null;
  }

  const dismissedUntil = Date.now() + DISMISS_DURATION_MS;

  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(dismissedUntil));
  } catch {
    return dismissedUntil;
  }

  return dismissedUntil;
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissedUntil, setDismissedUntil] = useState<number | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) {
      return;
    }

    setDismissedUntil(getDismissedUntil());
    setDeferredPrompt(window.__u12DeferredInstallPrompt ?? null);

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as BeforeInstallPromptEvent;
      window.__u12DeferredInstallPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
    };

    const handleInstalled = () => {
      window.__u12DeferredInstallPrompt = null;
      setDeferredPrompt(null);
      setAccepted(true);

      try {
        window.localStorage.removeItem(DISMISS_STORAGE_KEY);
      } catch {
        // Ignore storage access errors in private or locked-down browsing modes.
      }
    };

    const handleInstallAvailable = () => {
      setDeferredPrompt(window.__u12DeferredInstallPrompt ?? null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    window.addEventListener("u12-install-available", handleInstallAvailable);
    window.addEventListener("u12-install-installed", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      window.removeEventListener("u12-install-available", handleInstallAvailable);
      window.removeEventListener("u12-install-installed", handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);

    if (choice?.outcome === "accepted") {
      setAccepted(true);
    }

    window.__u12DeferredInstallPrompt = null;
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissedUntil(dismissForOneDay());
  };

  useEffect(() => {
    if (!dismissedUntil) {
      return;
    }

    const delay = dismissedUntil - Date.now();

    if (delay <= 0) {
      setDismissedUntil(null);
      return;
    }

    const timer = window.setTimeout(() => setDismissedUntil(null), delay);

    return () => window.clearTimeout(timer);
  }, [dismissedUntil]);

  if (accepted || dismissedUntil || isStandaloneMode() || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-4 z-[70] px-4">
      <div
        className={cn(
          "mx-auto flex max-w-xl items-start justify-between gap-4 rounded-2xl border border-black/10 bg-white/95 p-4 shadow-2xl backdrop-blur dark:border-white/10 dark:bg-neutral-950/95",
          "supports-[padding:max(0px)]:pb-[max(1rem,env(safe-area-inset-bottom))]"
        )}
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-black dark:text-white">Install Uncommon 12</p>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
            Add the app to your device for faster access and a standalone experience.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" onClick={() => void handleInstall()}>
            Install
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDismiss}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
